import { useTranslation } from 'react-i18next';
import { FadeIn } from '../components/ui/AnimatedSection';
import Calculator from '../features/pricing/Calculator';
import AuditForm from '../components/forms/AuditForm';
import FounderSection from '../features/home/FounderSection';
import { Link } from 'react-router-dom';
import { ShieldCheck, UserCheck, Star, Globe, MapPin } from 'lucide-react';

export default function Landing() {
  const { t, i18n } = useTranslation();

  return (
    <div className="min-h-screen flex flex-col font-sans text-black-deep bg-cream-100 selection:bg-gold-500 selection:text-white">
      
      {/* HEADER MINIMALISTE */}
      <header className="py-8 bg-white shadow-sm relative z-10">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <div className="w-16"></div> {/* Spacer for centering */}
          
          <Link to="/" className="inline-block" aria-label="Retour à l'accueil">
            <img src="/assets/logo_v2.webp" alt="L'art du Ménage" className="h-16 md:h-20 w-auto object-contain drop-shadow-md" width="400" height="150" />
          </Link>
          
          <div className="w-16 flex justify-end">
            <button 
              onClick={() => i18n.changeLanguage(i18n.language.startsWith('fr') ? 'en' : 'fr')}
              className="text-sm font-bold text-gold-600 hover:text-gold-500 transition-colors uppercase tracking-widest flex items-center gap-1"
              aria-label="Toggle language"
            >
              <Globe size={16} className="hidden sm:block" />
              {i18n.language.startsWith('fr') ? 'EN' : 'FR'}
            </button>
          </div>
        </div>
      </header>

      <main className="flex-grow">
        
        {/* HERO SECTION */}
        <section className="relative pt-20 pb-16 md:pt-32 md:pb-24 px-4 overflow-hidden bg-black-deep text-white">
          <div className="absolute inset-0 z-0 opacity-40">
            <div className="absolute inset-0 bg-gradient-to-b from-black-deep/90 via-black-deep/60 to-black-deep"></div>
            <img src="/assets/luxury_interior.webp" alt="Intérieur luxueux" fetchPriority="high" className="w-full h-full object-cover" />
          </div>
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <FadeIn>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif tracking-wide leading-tight mb-8">
                {t('landing.title')}
              </h1>
              <div className="w-24 h-1 bg-gold-500 mx-auto mb-8 rounded-full"></div>
              <p className="text-xl md:text-2xl font-light text-gray-300 max-w-2xl mx-auto mb-10">
                {t('landing.subtitle')}
              </p>
              <div className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm border border-gold-500/30 rounded-full px-6 py-3">
                <MapPin className="text-gold-400 w-5 h-5 flex-shrink-0" />
                <span className="text-white text-sm md:text-base font-light tracking-wide">
                  {t('common.exclusiveSector')}
                </span>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* METHOD SECTION */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
              
              <FadeIn delay={0.1} className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-cream-100 flex items-center justify-center mb-6 text-gold-600 shadow-sm border border-gold-200">
                  <ShieldCheck size={32} />
                </div>
                <h3 className="font-serif text-2xl mb-4 text-black-deep">{t('landing.methods.0.title')}</h3>
                <p className="text-gray-600 font-light leading-relaxed">
                  {t('landing.methods.0.desc')}
                </p>
              </FadeIn>

              <FadeIn delay={0.3} className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-cream-100 flex items-center justify-center mb-6 text-gold-600 shadow-sm border border-gold-200">
                  <UserCheck size={32} />
                </div>
                <h3 className="font-serif text-2xl mb-4 text-black-deep">{t('landing.methods.1.title')}</h3>
                <p className="text-gray-600 font-light leading-relaxed">
                  {t('landing.methods.1.desc')}
                </p>
              </FadeIn>

              <FadeIn delay={0.5} className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-cream-100 flex items-center justify-center mb-6 text-gold-600 shadow-sm border border-gold-200">
                  <Star size={32} />
                </div>
                <h3 className="font-serif text-2xl mb-4 text-black-deep">{t('landing.methods.2.title')}</h3>
                <p className="text-gray-600 font-light leading-relaxed">
                  {t('landing.methods.2.desc')}
                </p>
              </FadeIn>
              
            </div>
          </div>
        </section>

        {/* FOUNDER SECTION */}
        <FounderSection />

        {/* CALCULATOR / FILTER SECTION */}
        <Calculator hideAuditButton={true} vipMode={true} />

        {/* EMBEDDED AUDIT FORM */}
        <section id="audit-form" className="py-24 bg-white border-t border-gray-100">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <div className="bg-white p-8 md:p-12 shadow-[0_15px_40px_rgba(0,0,0,0.08)] border border-gold-200/30 rounded-sm">
                <AuditForm />
              </div>
            </FadeIn>
          </div>
        </section>

      </main>

      {/* FLOATING CTA */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-[90%] max-w-sm">
        <button 
          onClick={() => document.getElementById('audit-form')?.scrollIntoView({ behavior: 'smooth' })}
          className="w-full flex items-center justify-center bg-gradient-to-r from-gold-500 to-gold-400 text-white shadow-[0_8px_20px_rgba(200,160,80,0.4)] hover:shadow-[0_12px_25px_rgba(200,160,80,0.6)] hover:-translate-y-1 rounded-full transition-all duration-300 py-4 font-bold uppercase tracking-widest text-xs md:text-sm"
        >
          {t('landing.floatingCta')}
        </button>
      </div>

    </div>
  );
}
